/**
 * aides-industrie.ts
 * ─────────────────────────────────────────────────────────────────
 * Configuration centralisée des pages "stubs" /industrie/aides/[slug].
 *
 * Aides financières mobilisables par les grandes entreprises, les sites
 * industriels et les industriels multi-sites pour leurs projets
 * d'efficacité énergétique et de décarbonation des process.
 */

import type { AideStub } from './aides-particuliers';

export const AIDES_INDUSTRIE: AideStub[] = [
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'cee-ind',
    iconName: 'sparkles',
    eyebrow: "Certificats d'Économies d'Énergie, secteur Industrie",
    title: 'CEE IND-UT / IND-BA',
    shortLabel: 'CEE IND',
    lead: "Des aides pour vos équipements industriels : moteurs, variateurs, air comprimé, récupération de chaleur. Valable aussi pour l'isolation et l'éclairage de vos bâtiments.",
    amount: 'Selon fiche & volume',
    amountLabel: 'Versement après travaux',
    keyPoints: [
      {
        iconName: 'cog',
        label: 'Utilités industrielles',
        description:
          'Fiches couvrant variateurs de vitesse, moteurs haute efficacité, optimisation air comprimé, récupération de chaleur fatale, calorifugeage de réseaux process.',
      },
      {
        iconName: 'building-2',
        label: 'Bâti industriel',
        description:
          "Fiches d'isolation des bâtiments industriels (toitures, parois, planchers), éclairage LED haute efficacité, équipements d'eau chaude.",
      },
      {
        iconName: 'file-signature',
        label: 'Demande avant le devis',
        description:
          "L'aide doit être demandée AVANT la signature du devis. À défaut, dossier non éligible.",
      },
      {
        iconName: 'shield-check',
        label: 'Conservation des pièces 9 ans',
        description:
          'Conservation des justificatifs (devis, factures, attestations, photos chantier) pendant 9 ans en cas de vérification.',
      },
    ],
    conditions: [
      'Site industriel en activité, tout statut juridique',
      'Travaux ou équipements éligibles à une fiche IND-UT ou IND-BA applicable',
      "Demande d'aide avant la signature du devis",
      'Conservation des justificatifs pendant 9 ans : devis, factures, attestations',
      'Réalisation par un professionnel disposant des qualifications requises',
    ],
    cumulableWith: [
      'Bonus industriels',
      'Fonds chaleur ADEME',
      'Fonds décarbonation industrie',
      'Prêts à taux préférentiel',
    ],
    faq: [
      {
        question: 'Quelles fiches IND sont disponibles pour mon site ?',
        answer:
          "Les fiches Industrie se répartissent en deux familles : IND-UT pour les utilités (moteurs, variateurs, air comprimé, vapeur, froid, récupération de chaleur), et IND-BA pour le bâti industriel (isolation, éclairage, équipements). À partir de votre code NAF et de votre site, on liste les fiches qui s'appliquent et on chiffre les aides.",
      },
      {
        question: "L'aide IND est-elle cumulable avec le Fonds chaleur ?",
        answer:
          'Oui, dans la limite des plafonds autorisés. Le Fonds chaleur cible des opérations précises (biomasse, géothermie, solaire thermique, réseaux de chaleur). Les aides IND couvrent un périmètre plus large. On vérifie le cumul dossier par dossier.',
      },
      {
        question: 'Comment se passe le contrôle administratif ?',
        answer:
          "Le Pôle National peut diligenter un contrôle sur pièces ou sur site jusqu'à 9 ans après le dépôt. En tant que délégataire, nous gérons l'intégralité du contrôle : transmission des pièces, défense argumentée, suivi.",
      },
    ],
    ctaLabel: 'Estimer mes CEE industriels',
    ctaHref: '/industrie/diagnostic-cee',
    secondaryCta: {
      label: 'Voir le catalogue fiches industrie (IND)',
      href: '/cee/fiches?secteur=IND',
    },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'coups-de-pouce',
    iconName: 'flame',
    eyebrow: 'Bonifications CEE industrielles',
    title: 'Coups de pouce industriels',
    shortLabel: 'Coups de pouce IND',
    lead: "Un bonus qui s'ajoute à votre aide pour certaines opérations : récupération de chaleur, remplacement de moteurs, isolation des réseaux.",
    amount: 'Bonification CEE',
    amountLabel: 'Aide majorée pour opérations ciblées',
    keyPoints: [
      {
        iconName: 'flame',
        label: 'Opérations prioritaires ciblées',
        description:
          'Récupération de chaleur fatale (vapeur, air, eau process), motorisations haute efficacité, calorifugeage de réseaux haute température, optimisation air comprimé.',
      },
      {
        iconName: 'sparkles',
        label: "Cumulable avec l'aide de base",
        description:
          "Le bonus est une majoration de l'aide classique : les deux se cumulent automatiquement sur la même opération.",
      },
      {
        iconName: 'file-signature',
        label: 'Partenaires agréés',
        description: 'La majoration est versée via un partenaire agréé pour ce dispositif.',
      },
      {
        iconName: 'file-text',
        label: 'Périmètre évolutif',
        description:
          'Les opérations éligibles et les niveaux de majoration évoluent. Nous vérifions à chaque dossier les dispositifs actifs.',
      },
    ],
    conditions: [
      'Opération éligible à un dispositif de bonus industriel applicable',
      "Demande d'aide avant signature du devis",
      'Réalisation par un professionnel disposant des qualifications requises',
      'Versement via un partenaire agréé',
      'Conservation des pièces justificatives pendant 9 ans',
    ],
    cumulableWith: [
      'Aide IND de base',
      'Fonds chaleur ADEME',
      'Fonds décarbonation industrie',
      'Prêts à taux préférentiel',
    ],
    faq: [
      {
        question: "Quelle différence entre le bonus et l'aide IND classique ?",
        answer:
          "L'aide IND classique est versée pour toute opération éligible à une fiche IND-UT ou IND-BA. Le bonus est une majoration ciblée sur certaines opérations prioritaires (chaleur fatale, moteurs haute efficacité, calorifugeage). Le bonus s'ajoute à l'aide de base, sans s'y substituer.",
      },
      {
        question: 'Quels bonus industriels sont actifs ?',
        answer:
          "Les dispositifs évoluent. Historiquement, plusieurs bonus ont concerné l'industrie (récupération chaleur fatale, moteurs haute efficacité, calorifugeage). Nous vérifions les dispositifs applicables au moment de votre demande.",
      },
      {
        question: 'Le bonus est-il garanti après dépôt ?',
        answer:
          "La majoration est due dès lors que l'opération et le dispositif sont respectés. Un bonus engagé reste valable jusqu'à son terme, mais ne peut pas être réclamé rétroactivement après expiration.",
      },
    ],
    ctaLabel: 'Vérifier les Coups de pouce applicables',
    ctaHref: '/industrie/diagnostic-cee',
    secondaryCta: {
      label: 'Voir les fiches utilités industrielles (IND-UT)',
      href: '/cee/fiches?secteur=IND',
    },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'fonds-chaleur',
    iconName: 'thermometer',
    eyebrow: 'Programme ADEME, production de chaleur renouvelable',
    title: 'Fonds chaleur ADEME',
    shortLabel: 'Fonds chaleur',
    lead: "Une aide pour vos projets de chaleur renouvelable : biomasse, géothermie, solaire thermique, récupération de chaleur. Pour l'industrie, le tertiaire et l'agriculture.",
    amount: 'Subvention ADEME',
    amountLabel: "Taux d'aide selon dispositif et zonage",
    keyPoints: [
      {
        iconName: 'thermometer',
        label: 'Filières renouvelables',
        description:
          'Biomasse, géothermie (sondes ou nappe), solaire thermique, réseaux de chaleur renouvelables, valorisation de chaleur fatale issue de process industriels.',
      },
      {
        iconName: 'landmark',
        label: "Géré par l'ADEME",
        description:
          "Instruction par les directions régionales de l'ADEME, sur la base d'un cahier des charges national.",
      },
      {
        iconName: 'banknote',
        label: 'Subvention directe',
        description:
          'Aide forfaitaire ou proportionnelle aux énergies renouvelables produites, selon le dispositif. Versée par tranches selon avancement du projet.',
      },
      {
        iconName: 'sparkles',
        label: 'Cumul possible avec aides',
        description:
          'Les aides travaux et le Fonds chaleur sont cumulables, dans la limite des plafonds autorisés.',
      },
    ],
    conditions: [
      "Projet de production de chaleur d'origine renouvelable ou de récupération éligible au cahier des charges applicable",
      'Site industriel, agricole, tertiaire ou collectivité',
      "Étude de faisabilité énergétique préalable (généralement cofinancée par l'ADEME)",
      "Dépôt du dossier auprès de la direction régionale de l'ADEME",
      "Respect des règles européennes sur les aides d'État",
    ],
    cumulableWith: [
      'Aides IND',
      'Bonus industriels',
      'Fonds décarbonation industrie',
      'Prêts à taux préférentiel',
    ],
    faq: [
      {
        question: 'Quels projets industriels peuvent prétendre au Fonds chaleur ?',
        answer:
          'Plusieurs typologies : chaudière biomasse pour process ou bâtiment, géothermie sur sondes ou nappe pour chauffage de bâti, solaire thermique pour pré-chauffage process, valorisation de chaleur fatale (fumées, condensats, air process). Le cahier des charges précise les seuils minimaux.',
      },
      {
        question: "Quel est le délai d'instruction ?",
        answer:
          "Variable selon la direction régionale et la complexité du projet. Comptez en général plusieurs mois entre le dépôt et la décision. Le dépôt s'effectue sur dossier complet (étude de faisabilité, devis, plan de financement, calendrier).",
      },
      {
        question: 'Le Fonds chaleur est-il cumulable avec les aides travaux ?',
        answer:
          "Oui, dans la limite des plafonds autorisés. Le Fonds chaleur couvre l'investissement en équipement de production renouvelable ; les aides travaux couvrent plus largement les actions d'efficacité énergétique. Le cumul est souvent pertinent sur les projets à fort contenu renouvelable.",
      },
    ],
    ctaLabel: 'Étudier mon éligibilité Fonds chaleur',
    ctaHref: '/contact?source=industrie&besoin=fonds-chaleur',
    secondaryCta: { label: "Site officiel de l'ADEME", href: 'https://www.ademe.fr/' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'fonds-decarbonation',
    iconName: 'banknote',
    eyebrow: "France 2030, décarbonation de l'industrie",
    title: 'Fonds décarbonation industrie',
    shortLabel: 'Décarbonation industrie',
    lead: 'Des aides pour décarboner vos process : efficacité énergétique, électrification, chaleur bas-carbone. Cumulable avec les primes.',
    amount: 'Programme pluriannuel',
    amountLabel: 'Subventions, avances remboursables, prêts',
    keyPoints: [
      {
        iconName: 'banknote',
        label: 'Plusieurs guichets',
        description:
          'Bpifrance (programmes de décarbonation industrielle), ADEME (appels à projets dédiés), opérateurs sectoriels selon la filière.',
      },
      {
        iconName: 'sparkles',
        label: "Plusieurs types d'aides",
        description:
          'Subventions directes, avances remboursables (remboursées en cas de succès commercial), prêts à taux préférentiel ou avec garantie partielle.',
      },
      {
        iconName: 'cog',
        label: 'Projets de transformation',
        description:
          "Électrification de process, substitution d'énergies fossiles, récupération massive de chaleur, captage et utilisation de CO₂, hydrogène bas-carbone industriel.",
      },
      {
        iconName: 'file-search',
        label: 'Dossier souvent multipartite',
        description:
          'Pour les projets significatifs, dossier souvent porté par un consortium industrie-recherche, avec ingénierie financière dédiée. Réservé aux projets matures.',
      },
    ],
    conditions: [
      'Projet correspondant aux priorités sectorielles du Fonds décarbonation',
      "Dépôt dans le cadre d'un appel à projets identifié (Bpifrance, ADEME, autre opérateur)",
      'Capacité à apporter un cofinancement propre, taux variable selon le dispositif',
      'Caractère démonstratif, innovant ou réplicable du projet',
      "Respect des règles européennes sur les aides d'État",
    ],
    cumulableWith: [
      'Aides IND',
      'Fonds chaleur ADEME',
      'Prêts à taux préférentiel',
      'Aides régionales décarbonation',
    ],
    faq: [
      {
        question: 'Quels dispositifs composent le Fonds décarbonation ?',
        answer:
          'Plusieurs guichets coexistent : appels à projets ADEME, guichets Bpifrance, dispositifs sectoriels. Le cadre global est piloté dans le cadre de France 2030.',
      },
      {
        question: 'Mon investissement courant est-il éligible ?',
        answer:
          "Plus rarement. Le Fonds décarbonation cible principalement des projets structurants à fort effet de levier (démonstration, première industrielle, transformation majeure). Pour les investissements courants d'efficacité énergétique, orientez-vous vers les aides IND et le Fonds chaleur ADEME.",
      },
      {
        question: 'Quels sont les délais et démarches ?',
        answer:
          "Les appels à projets sont publiés régulièrement avec une fenêtre de dépôt. L'instruction prend plusieurs mois, suivie d'une convention. Pour les projets importants, comptez 12 à 18 mois entre l'idée et le décaissement. Nous vous aidons à identifier l'appel pertinent.",
      },
    ],
    ctaLabel: 'Identifier mon éligibilité décarbonation',
    ctaHref: '/contact?source=industrie&besoin=decarbonation',
    secondaryCta: {
      label: 'Programme France 2030',
      href: 'https://www.gouvernement.fr/france-2030',
    },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'prets-bpifrance',
    iconName: 'piggy-bank',
    eyebrow: 'Bpifrance, prêts climat & efficacité énergétique',
    title: 'Prêts Bpifrance climat',
    shortLabel: 'Prêts Bpifrance',
    lead: "Des prêts à conditions avantageuses pour financer vos projets d'efficacité énergétique. Complément idéal quand les subventions ne couvrent pas tout.",
    amount: 'Prêt préférentiel',
    amountLabel: 'Sans garantie ni caution personnelle',
    keyPoints: [
      {
        iconName: 'piggy-bank',
        label: 'Plusieurs prêts dédiés',
        description:
          "Prêt Vert (transition énergétique), Prêt Économies d'Énergie (efficacité), Prêts Verts secteur industrie — chacun avec son périmètre et ses conditions.",
      },
      {
        iconName: 'shield-check',
        label: 'Sans garantie personnelle',
        description:
          'Ces prêts sont accordés sans garantie réelle ni caution personnelle du dirigeant. Un vrai atout pour financer la décarbonation sans bloquer votre trésorerie.',
      },
      {
        iconName: 'banknote',
        label: 'Taux préférentiel',
        description:
          "Taux bonifiés par rapport au marché bancaire classique, grâce au partenariat avec l'ADEME ou aux ressources publiques mobilisées.",
      },
      {
        iconName: 'sparkles',
        label: 'Cumulable avec aides directes',
        description:
          "Ces prêts complètent les subventions pour boucler le plan de financement d'un projet.",
      },
    ],
    conditions: [
      'Entreprise française, immatriculée et en activité, généralement TPE/PME/ETI',
      "Projet d'efficacité énergétique ou de transition énergétique correspondant au périmètre du prêt",
      'Capacité de remboursement vérifiée par Bpifrance (analyse financière classique)',
      'Devis et plan de financement détaillés du projet',
      "Engagement à atteindre les objectifs d'économies d'énergie ou de production renouvelable",
    ],
    cumulableWith: [
      'Aides IND',
      'Bonus industriels',
      'Fonds chaleur ADEME',
      'Fonds décarbonation industrie',
    ],
    faq: [
      {
        question: "Quelle différence entre le Prêt Vert et le Prêt Économies d'Énergie ?",
        answer:
          "Le Prêt Vert (distribué en partenariat avec l'ADEME) cible les projets globaux de transition énergétique avec montants élevés. Le Prêt Économies d'Énergie cible spécifiquement les actions d'efficacité énergétique avec un périmètre plus restreint. Les conditions exactes évoluent — vérifiez auprès de Bpifrance.",
      },
      {
        question: 'Comment demander un prêt Bpifrance ?',
        answer:
          'Trois pistes : (1) votre interlocuteur bancaire qui peut co-instruire avec Bpifrance ; (2) le site bpifrance.fr pour déposer un pré-dossier en ligne ; (3) votre direction régionale Bpifrance pour les projets complexes. Nous vous accompagnons sur le montage financier.',
      },
      {
        question: 'Le prêt se rembourse-t-il sur les économies réalisées ?',
        answer:
          "Le prêt classique se rembourse selon un échéancier fixe. Certains dispositifs comportent des différés d'amortissement adaptés au calendrier des économies d'énergie réelles. Le montage optimal combine l'aide versée rapidement (trésorerie immédiate) et le prêt amorti sur la durée des économies.",
      },
    ],
    ctaLabel: 'Étudier mon plan de financement',
    ctaHref: '/contact?source=industrie&besoin=financement',
    secondaryCta: { label: 'Site Bpifrance', href: 'https://www.bpifrance.fr/' },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'optimisation-turpe',
    iconName: 'trending-down',
    eyebrow: "Tarif d'acheminement, fixé par la CRE",
    title: 'Optimisation TURPE site industriel',
    shortLabel: 'Optimisation TURPE',
    lead: 'La partie transport de votre facture. Ça ne se négocie pas, mais ça se pilote : puissances souscrites, dépassements, énergie réactive. Un audit révèle souvent des économies significatives.',
    amount: 'Économies récurrentes',
    amountLabel: 'Sur la part acheminement de chaque point de livraison',
    keyPoints: [
      {
        iconName: 'gauge',
        label: 'Puissances par classe temporelle',
        description:
          'En haute tension, les puissances se souscrivent par classe (pointe, heures pleines/creuses, saison). On recale chaque classe sur les appels réels mesurés par vos courbes de charge.',
      },
      {
        iconName: 'cog',
        label: 'Énergie réactive traitée',
        description:
          "La facturation d'énergie réactive est un poste souvent ignoré : on la quantifie et on arbitre (compensation si le retour sur investissement le justifie).",
      },
      {
        iconName: 'trending-down',
        label: 'Dépassements maîtrisés',
        description:
          'Démarrages de process, pointes synchronisées : les dépassements se paient avec pénalité. On identifie les causes et on arbitre.',
      },
      {
        iconName: 'wallet',
        label: "Indépendant des fournisseurs d'énergie",
        description:
          "Le transport est identique quel que soit le fournisseur : l'optimisation se cumule avec votre stratégie d'achat d'énergie.",
      },
    ],
    conditions: [
      'Analyse des courbes de charge ou des 12 derniers mois de factures',
      'Applicable à tous les segments (basse et haute tension), quel que soit le fournisseur',
      'Modification des paramètres via le fournisseur ou le gestionnaire de réseau',
      "Étude de l'énergie réactive et des pénalités de dépassement par point de livraison",
    ],
    cumulableWith: [
      'Courtage énergie industriel',
      'Exonération CSPE',
      'Aides industriels',
      'Fonds décarbonation',
    ],
    faq: [
      {
        question: 'Quels paramètres sont optimisables sur un site ?',
        answer:
          "Principalement : les puissances souscrites par classe temporelle, la version tarifaire (courte/moyenne/longue utilisation selon le profil), les pénalités de dépassement et l'énergie réactive. Chaque point de livraison a son optimum propre.",
      },
      {
        question: "L'optimisation impose-t-elle d'arrêter le process ?",
        answer:
          "Non. La majorité des gains vient d'un recalage administratif des souscriptions et de la version tarifaire, sans toucher au process. Lorsque le lissage de certaines charges apporte un gain supplémentaire, il est proposé en option, jamais imposé.",
      },
      {
        question: 'À quelle fréquence réviser les souscriptions ?',
        answer:
          "À chaque évolution significative du site (nouvelle ligne, extension, changement de rythme) et lors de chaque révision tarifaire. Une revue annuelle systématique, intégrée au reporting énergie, évite que l'optimisation se dégrade.",
      },
    ],
    ctaLabel: 'Demander un audit acheminement',
    ctaHref: '/industrie/comparateur-energie',
    secondaryCta: {
      label: 'En parler avec un référent',
      href: '/contact?source=industrie&besoin=optimisation-turpe',
    },
  },
  // ──────────────────────────────────────────────────────────────
  {
    slug: 'exoneration-cspe',
    iconName: 'percent',
    eyebrow: "Fiscalité de l'électricité, taux réduits et exonérations,",
    title: "Exonération CSPE & taux réduits d'accise",
    shortLabel: 'Exonération CSPE',
    lead: 'Une taxe sur chaque kWh consommé. Les sites électro-intensifs paient moins, certains procédés sont exonérés. Si vous avez trop payé, vous pouvez récupérer le trop-versé.',
    amount: 'Taux réduit ou exonération',
    amountLabel: "Selon l'électro-intensité et les procédés du site",
    keyPoints: [
      {
        iconName: 'cog',
        label: 'Procédés exonérés par nature',
        description:
          "Électrolyse, réduction chimique, procédés métallurgiques et minéralogiques : ces usages sont hors champ ou exonérés. L'identification se fait usage par usage, compteur par compteur.",
      },
      {
        iconName: 'gauge',
        label: 'Électro-intensité calculée',
        description:
          "Pour les autres usages, l'éligibilité aux taux réduits repose sur le ratio entre consommation et valeur ajoutée — calculé à partir de votre liasse fiscale et de vos factures.",
      },
      {
        iconName: 'banknote',
        label: 'Réclamation sur le passé',
        description:
          'Si le taux plein a été appliqué à tort, la réclamation récupère le trop-versé sur les années récentes. Sur un site électro-intensif, le montant peut être significatif.',
      },
      {
        iconName: 'file-signature',
        label: 'Conformité durable',
        description:
          'Attestations transmises aux fournisseurs pour chaque point de livraison, procédure documentée pour les exercices suivants.',
      },
    ],
    conditions: [
      'Cartographie des usages du site (procédés exonérés vs usages standards)',
      "Calcul du ratio d'électro-intensité sur les exercices concernés",
      'Attestations transmises aux fournisseurs pour chaque point de livraison',
      'Dossier justificatif complet (factures, comptabilité, descriptif des procédés) pour les réclamations',
    ],
    cumulableWith: [
      'Optimisation TURPE',
      'Aides industriels',
      'Fonds chaleur ADEME',
      'Fonds décarbonation',
    ],
    faq: [
      {
        question: 'Quels procédés industriels sont exonérés ?',
        answer:
          "Les textes visent notamment l'électrolyse, la réduction chimique, et les procédés métallurgiques et minéralogiques. L'électricité consommée par ces procédés est exonérée — encore faut-il pouvoir l'isoler (sous-comptage ou méthode de répartition documentée). C'est précisément l'objet de la cartographie des usages qu'on réalise sur site.",
      },
      {
        question: "Comment se calcule l'électro-intensité ?",
        answer:
          "Le critère repose sur le rapport entre la consommation et la valeur ajoutée de l'entreprise, avec des seuils fixés par les textes applicables. Plus le site est énergivore au regard de sa valeur ajoutée, plus le taux applicable diminue. Le calcul se fait à partir de votre liasse fiscale.",
      },
      {
        question: "Sur combien d'années peut-on réclamer ?",
        answer:
          "Le délai de réclamation fiscale est limité — en pratique l'année en cours et les deux précédentes. Chaque année qui passe fait perdre un exercice récupérable. La réclamation se prépare avec un dossier solide : consommations, procédés, données comptables.",
      },
    ],
    ctaLabel: 'Vérifier mon éligibilité',
    ctaHref: '/contact?source=industrie&besoin=exoneration-cspe',
    secondaryCta: {
      label: 'Audit acheminement en parallèle',
      href: '/industrie/comparateur-energie',
    },
  },
];

export function getAideIndustrieStub(slug: string): AideStub | undefined {
  return AIDES_INDUSTRIE.find((a) => a.slug === slug);
}
